import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { useLang, LANGS } from "@/i18n";

const LINKS = [
  { k: "nav_about", href: "#about" },
  { k: "nav_services", href: "#services" },
  { k: "nav_plans", href: "#plans" },
  { k: "nav_work", href: "#work" },
  { k: "nav_team", href: "#team" },
  { k: "nav_contact", href: "#contact" },
];

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  const langMenu = (
    <AnimatePresence>
      {langOpen && (
        <motion.div
          data-testid="lang-menu"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25 }}
          className="absolute right-0 top-full mt-2 min-w-[140px] sketch-border bg-[#FFFFFF] shadow-[4px_4px_0_#8B5CF6] overflow-hidden z-50"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => {
                setLang(l.code);
                setLangOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                lang === l.code ? "bg-[#F2C230] text-[#2B0A4E]" : "text-[#2B0A4E] hover:bg-[#F0E9FF]"
              }`}
            >
              {l.name}
            </button>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled ? "bg-[#FDFBF5]/80 backdrop-blur-xl shadow-[0_1px_0_rgba(43,10,78,0.08)]" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-20 sm:h-24">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="flex items-center gap-3 group"
        >
          <img src="/assets/logo-cat.png" alt="KATZYA logo" className="h-11 sm:h-12 w-auto transition-transform duration-500 group-hover:-rotate-6" />
          <span className="font-serif-display font-bold text-xl sm:text-2xl tracking-[0.18em] text-[#2B0A4E]">KATZYA</span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.k.replace("nav_", "")}`}
              onClick={() => go(l.href)}
              className="font-mono-label text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#2B0A4E] hover:text-[#8B5CF6] transition-colors duration-300 relative after:absolute after:-bottom-1.5 after:left-0 after:h-[2.5px] after:w-0 after:bg-[#F2C230] after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {t(l.k)}
            </button>
          ))}
          <div className="relative">
            <button
              data-testid="lang-toggle"
              onClick={() => setLangOpen(!langOpen)}
              aria-label="Change language"
              className="flex items-center gap-1.5 font-mono-label text-xs tracking-[0.15em] uppercase text-[#2B0A4E] sketch-border px-3.5 py-2 hover:bg-[#8B5CF6]/15 transition-colors duration-300"
            >
              <Globe size={15} /> {lang.toUpperCase()}
            </button>
            {langMenu}
          </div>
          <button
            data-testid="nav-cta-button"
            onClick={() => go("#contact")}
            className="sketch-border px-7 py-3 font-mono-label text-xs sm:text-sm tracking-[0.2em] uppercase text-[#2B0A4E] bg-[#F2C230] hover:bg-[#2B0A4E] hover:text-[#FDFBF5] transition-colors duration-300"
          >
            {t("nav_cta")}
          </button>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <div className="relative">
            <button
              data-testid="lang-toggle-mobile"
              onClick={() => setLangOpen(!langOpen)}
              aria-label="Change language"
              className="flex items-center gap-1 font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#2B0A4E] sketch-border px-3 py-2"
            >
              <Globe size={14} /> {lang.toUpperCase()}
            </button>
            {langMenu}
          </div>
          <button
            data-testid="nav-mobile-toggle"
            className="text-[#2B0A4E] p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#FDFBF5]/95 backdrop-blur-xl border-t border-[#2B0A4E]/10"
          >
            <div className="flex flex-col px-6 py-4 gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.k.replace("nav_", "")}`}
                  onClick={() => go(l.href)}
                  className="text-left font-serif-display text-2xl py-3 text-[#2B0A4E] border-b border-[#2B0A4E]/8"
                >
                  {t(l.k)}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
